import * as THREE from 'three';
import { Random } from '../core/Random';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, M, mat, mesh, texMat } from './materials';
import { PublicRoom } from './PublicRoom';
import { COOTER_SLOT, OFFICE_SLOT } from './rooms';
import {
  corkBoardTexture,
  linoleumTexture,
  panelingTexture,
  shagTexture,
  signTexture,
  type SignSpec,
} from './textures';

/**
 * Two little offices in trailers: the park office (Darlene is never in it, but the
 * rent slot is) and Cooter's sales office, where he sits behind a steel-mesh window
 * with a pegboard of keys to cars you shouldn't buy. Local coordinates; door on +z.
 */

export const OFFICE_SPOTS = {
  slot: { x: -0.6, z: -0.45 },
  board: { x: 2.6, z: -1.75 },
} as const satisfies Record<string, Vec2>;

export const COOTER_SPOTS = {
  cooter: { x: -0.4, z: -1.0 },
  window: { x: -0.4, z: 0.6 },
} as const satisfies Record<string, Vec2>;

function sign(spec: SignSpec, w: number, h: number): THREE.Mesh {
  return mesh(new THREE.PlaneGeometry(w, h), texMat(signTexture(spec)), 0, 0, 0, { cast: false });
}

/** A counter across the room (along x) with a steel-mesh window over it. */
function windowCounter(
  width: number,
  height: number,
  top: number,
): { counter: THREE.Group; mesh: THREE.Group } {
  const counter = new THREE.Group();
  counter.add(box(width, 1.0, 0.6, mat('#6b5a48'), 0, 0.5, 0));
  counter.add(box(width + 0.04, 0.05, 0.7, mat('#c9b48a'), 0, 1.025, 0.02));
  const grill = new THREE.Group();
  const wire = mat('#9aa0a6', { metal: 0.6, rough: 0.5, transparent: true, opacity: 0.6 });
  grill.add(box(width, top - 1.05, 0.02, wire, 0, 1.05 + (top - 1.05) / 2, -0.25));
  for (const x of [-width / 2, 0, width / 2])
    grill.add(box(0.06, top - 1.05, 0.06, M.steel, x, 1.05 + (top - 1.05) / 2, -0.25));
  grill.add(box(0.5, 0.18, 0.04, M.black, 0, 1.12, -0.24));
  void height;
  return { counter, mesh: grill };
}

export interface Office {
  room: PublicRoom;
  slot: THREE.Object3D;
}

export function buildParkOfficeInterior(parent: THREE.Object3D, collision: CollisionWorld): Office {
  let slot: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    OFFICE_SLOT,
    'single',
    {
      wall: texMat(panelingTexture(), { color: '#d9c0a0' }),
      floor: texMat(linoleumTexture(), { color: '#e8dcc8' }),
      ceiling: mat('#ece6da', { rough: 1 }),
    },
    (kit) => {
      const { depth: D, height: H } = kit.shape;
      // Darlene's half of the office, behind a window that's always got a sign in it.
      const w = windowCounter(4.4, 0, H);
      kit.add(w.counter, -0.6, -0.95);
      kit.add(w.mesh, -0.6, -0.95);
      kit.solid(-0.6, -0.95, 4.4, 0.7);
      kit.solid(-2.85, -1.35, 0.1, 0.9);
      kit.solid(1.65, -1.35, 0.1, 0.9);
      const back = sign(
        {
          w: 384,
          h: 192,
          bg: '#fff7d6',
          fg: '#b31d1d',
          border: '#b31d1d',
          borderWidth: 8,
          lines: [
            { text: 'BACK IN 5 MIN', size: 52, font: 'Anton' },
            { text: '(SINCE 2009)', size: 34, font: 'Anton', color: '#333' },
          ],
        },
        0.6,
        0.3,
      );
      back.position.set(-0.6, 1.35, -1.19);
      kit.place(back, -0.6, -1.19);
      back.position.y = 1.35;
      // The rent slot: a steel box on the counter.
      const box2 = new THREE.Group();
      box2.add(box(0.5, 0.35, 0.35, mat('#3b5b8c', { metal: 0.4 }), 0, 1.22, 0));
      box2.add(box(0.3, 0.02, 0.37, M.black, 0, 1.33, 0));
      slot = kit.place(box2, OFFICE_SPOTS.slot.x, -0.85);
      // Her desk behind the window: a computer from 1998, a cat calendar, a fax.
      kit.add(box(1.6, 0.06, 0.7, mat('#8a6a48'), 0, 0.76, 0), -1.4, -1.45);
      kit.add(box(0.4, 0.35, 0.35, mat('#d9d4c7'), 0, 0.97, 0), -1.7, -1.5);
      kit.add(
        box(0.36, 0.28, 0.02, mat('#2a5a3a', { emissive: '#3a8a5a', emissiveIntensity: 0.4 }), 0, 0.98, 0),
        -1.7,
        -1.32,
      );
      kit.add(box(0.4, 0.15, 0.3, mat('#5a5652'), 0, 0.87, 0), -0.9, -1.5);
      kit.solid(-1.4, -1.45, 1.6, 0.7);
      // Customer side: two chairs nobody sits in, a water cooler, the notice board.
      kit.add(
        cyl(0.16, 0.16, 0.5, mat('#9fdcff', { transparent: true, opacity: 0.7 }), 0, 1.25, 0, 10),
        4.6,
        -1.4,
      );
      kit.add(box(0.36, 1.0, 0.36, mat('#e8e8e8'), 0, 0.5, 0), 4.6, -1.4);
      kit.solid(4.6, -1.4, 0.4, 0.4);
      for (const x of [1.4, 2.2]) {
        kit.add(box(0.48, 0.05, 0.45, mat('#c2185b'), 0, 0.45, 0), x, 1.4);
        kit.add(box(0.48, 0.45, 0.05, mat('#c2185b'), 0, 0.7, 0), x, 1.6);
      }
      const board = mesh(new THREE.PlaneGeometry(1.4, 0.9), texMat(corkBoardTexture()), 0, 1.5, 0, {
        cast: false,
      });
      board.position.set(OFFICE_SPOTS.board.x, 1.55, -D / 2 + 0.03);
      kit.walls.back.add(board);
      kit.walls.back.add(box(1.5, 1.0, 0.03, mat('#5a3a1e'), OFFICE_SPOTS.board.x, 1.55, -D / 2 + 0.015));
      const rules = sign(
        {
          w: 512,
          h: 320,
          bg: '#ffffff',
          fg: '#b0417a',
          border: '#b0417a',
          borderWidth: 10,
          lines: [
            { text: 'PARK RULES', size: 60, font: 'Rye' },
            { text: '1. RENT FRIDAY 5 PM', size: 34, font: 'Anton', color: '#111' },
            { text: '2. NO FIREWORKS (LOL)', size: 34, font: 'Anton', color: '#111' },
            { text: '3. SEE RULE 1', size: 34, font: 'Anton', color: '#111' },
          ],
        },
        0.8,
        0.5,
      );
      rules.position.set(4.9, 1.6, -D / 2 + 0.02);
      kit.walls.back.add(rules);
      void Random;
      void shagTexture;
    },
  );
  return { room, slot };
}

export interface CooterOffice {
  room: PublicRoom;
  window: THREE.Object3D;
}

export function buildCooterInterior(parent: THREE.Object3D, collision: CollisionWorld): CooterOffice {
  let window: THREE.Object3D = new THREE.Group();
  const room = new PublicRoom(
    parent,
    collision,
    COOTER_SLOT,
    'single',
    {
      wall: texMat(panelingTexture(), { color: '#a07850' }),
      floor: texMat(shagTexture('#5a6b3a'), { rough: 1 }),
      ceiling: mat('#d9d4c8', { rough: 1 }),
    },
    (kit) => {
      const { depth: D, height: H } = kit.shape;
      const rng = new Random(23);
      const w = windowCounter(3.2, 0, H);
      kit.add(w.counter, COOTER_SPOTS.window.x, -0.2);
      window = kit.place(w.mesh, COOTER_SPOTS.window.x, -0.2);
      kit.solid(COOTER_SPOTS.window.x, -0.2, 3.2, 0.7);
      kit.solid(-2.05, -1.0, 0.1, 1.6);
      kit.solid(1.25, -1.0, 0.1, 1.6);
      // The key pegboard: one tag per car you shouldn't buy.
      kit.add(box(1.6, 0.9, 0.03, mat('#c9a26a'), 0, 1.55, 0), -0.4, -D / 2 + 0.03);
      for (let i = 0; i < 16; i++) {
        const tag = box(
          0.06,
          0.1,
          0.01,
          mat(rng.pick(['#c62828', '#ffd400', '#1d4e9e', '#2f7d32'])),
          0,
          0,
          0,
        );
        kit.add(tag, -1.0 + (i % 8) * 0.17, -D / 2 + 0.05);
        tag.position.y = 1.75 - Math.floor(i / 8) * 0.3;
      }
      kit.add(box(1.4, 0.06, 0.6, mat('#6b4a2b'), 0, 0.76, 0), -0.4, -1.4);
      kit.solid(-0.4, -1.4, 1.4, 0.6);
      const finance = sign(
        {
          w: 512,
          h: 256,
          bg: '#ffd400',
          fg: '#c62828',
          border: '#111',
          borderWidth: 10,
          lines: [
            { text: 'FINANCING', size: 70, font: 'Bangers' },
            { text: 'AVAILABLE*', size: 70, font: 'Bangers', color: '#111' },
            { text: '*NOT REALLY', size: 26, font: 'Anton', color: '#111' },
          ],
        },
        0.9,
        0.45,
      );
      finance.position.set(2.4, 1.65, -D / 2 + 0.02);
      kit.walls.back.add(finance);
      const tires = sign(
        {
          w: 512,
          h: 128,
          bg: '#ffffff',
          fg: '#111',
          border: '#c62828',
          borderWidth: 8,
          lines: [{ text: 'NO TIRE KICKERS', size: 64, font: 'Anton' }],
        },
        1.0,
        0.25,
      );
      tires.position.set(COOTER_SPOTS.window.x, 2.15, 0.15);
      kit.place(tires, COOTER_SPOTS.window.x, 0.15);
      tires.position.y = 2.15;
      // A coffee pot that's been on since Tuesday, and a waiting couch.
      kit.add(box(0.3, 0.35, 0.25, M.black, 0, 0.95, 0), -1.4, -1.55);
      kit.add(box(2.0, 0.45, 0.85, mat('#7a3a2a'), 0, 0.3, 0), -3.9, 1.3);
      kit.add(box(2.0, 0.5, 0.2, mat('#7a3a2a'), 0, 0.7, 0), -3.9, 1.65);
      kit.solid(-3.9, 1.4, 2.0, 0.9);
    },
  );
  return { room, window };
}
